import { baseApi } from './baseApi';
import { Unit } from 'models';
import { QueryParams, TAG_TYPES, CheckUsedResponse, ReplaceRequest, ReplaceResponse } from 'types';
import { getQueryParams } from 'utils';

const URL_UNIT = 'units';

const unitApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUnits: builder.query<
      {
        data: {
          rows: Unit[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_UNIT, method: 'GET' };
        return {
          url: `${URL_UNIT}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Unit, id })),
              { type: TAG_TYPES.Unit, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Unit, id: 'LIST' }];
      },
    }),

    getUnitById: builder.query<Unit, string>({
      query: (id) => ({
        url: `${URL_UNIT}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Unit, id }],
    }),

    createUnit: builder.mutation<Unit, Omit<Unit, 'id'>>({
      query: (data) => ({
        url: URL_UNIT,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Unit, id: 'LIST' }],
    }),

    updateUnit: builder.mutation<Unit, Partial<Unit> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_UNIT}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Unit, id },
        { type: TAG_TYPES.Unit, id: 'LIST' },
      ],
    }),

    checkUnitUsage: builder.query<CheckUsedResponse, string>({
      query: (id) => ({
        url: `${URL_UNIT}/${id}/check-used`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Unit, id: `CHECK_USED_${id}` }],
    }),

    deactivateUnit: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_UNIT}/${id}/deactivate`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Unit, id },
        { type: TAG_TYPES.Unit, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
      ],
    }),

    deleteUnitWithReplacement: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_UNIT}/${id}/delete`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Unit, id },
        { type: TAG_TYPES.Unit, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetUnitsQuery: useGetAllUnitsQuery,
  useLazyGetUnitsQuery: useLazyGetAllUnitsQuery,
  useGetUnitByIdQuery,
  useCreateUnitMutation,
  useUpdateUnitMutation,
  useLazyCheckUnitUsageQuery,
  useDeactivateUnitMutation,
  useDeleteUnitWithReplacementMutation,
} = unitApiService;
